import { AppBar, LinearProgress, Toolbar, Tooltip } from "@material-ui/core";
import { createStyles, CSSProperties, makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import * as React from "react";
import { Link as RouterLink } from "react-router-dom";

import {
    Link,
    PageActionButton,
    PageType,
    RelodeTheme,
    Text,
    useRelodeAppUI,
} from "../";
import { ArrowLeftIcon } from "../../assets/icons";
import { mergeClasses } from "../styles/mergeClasses";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                backgroundColor: "transparent",
                boxShadow: "none",
            },
            toolbar: {
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
                display: "flex",
                flexDirection: "row",
            },
            headerText: {
                whiteSpace: "nowrap",
                overflow: "hidden",
                lineHeight: "normal",
                textOverflow: "ellipsis",
                maxWidth: "400px",
                paddingRight: theme.spacing(1),
                paddingLeft: theme.spacing(1),

                [theme.breakpoints.down("md")]: {
                    fontSize: "1.2rem",
                    maxWidth: 300,
                    display: "inline-block",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                },
            },
            actions: {
                flexGrow: 1,
            },
            busy: {
                height: 5,
                backgroundColor: theme.palette.secondary.dark,
            },
            notBusy: {
                height: 5,
                backgroundColor: "transparent",
            },
            pageSecondary: {
                color: theme.relode.colors.pageSecondaryColor,
                wordWrap: "break-word",
            },
            pagePrimary: {
                color: theme.relode.colors.pagePrimaryColor,
                wordWrap: "break-word",
            },
            shadow: {
                boxShadow:
                    "0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14)",
                paddingBottom: 4,
                overflow: "hidden",
                marginTop: -4,
            },
            labelOuter: {
                display: "flex",
                flexDirection: "column",
            },
            labelInner: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
            },
            statusLabel: {
                marginRight: theme.spacing(0.5),
            },
            subtitle: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: theme.spacing(0, 1),
            },
        }),
    { name: "PageHeader" }
);

export type PageHeaderCSSProperties =
    | "root"
    | "toolbar"
    | "headerText"
    | "smallTextSize"
    | "actions"
    | "busy"
    | "notBusy"
    | "pageSecondary"
    | "pagePrimary"
    | "shadow";
export type PageHeaderStyles = Partial<
    Record<
        PageHeaderCSSProperties,
        CSSProperties | ((props?: {}) => CSSProperties)
    >
>;
interface Props {
    classes?: Partial<ReturnType<typeof useStyles>>;
    children?: React.ReactNode;
    headerInfo?: React.ReactNode;
    title?: React.ReactNode;
    subtitle?: React.ReactNode;
    className?: string;
    backUrl?: string;
    label?: string;
    labelLinkURL?: string;
    statusLabel?: React.ReactNode;
    pageTitle?: string;
    isBusy?: boolean;
    pageType: PageType;
    showHeaderShadow?: boolean;
}

export const PageHeader = React.memo((props: Props) => {
    const classes = mergeClasses(useStyles({}), props.classes);
    const { ui } = useRelodeAppUI();
    const [showLoading, setShowLoading] = React.useState(false);

    React.useEffect(() => {
        let timer;
        if (!props.isBusy) {
            setShowLoading(false);
        } else {
            timer = setTimeout(() => setShowLoading(true), 200);
        }
        return () => !!timer && clearTimeout(timer);
    }, [props.isBusy]);

    React.useEffect(() => {
        if (props.pageTitle) {
            ui.actions.setPageTitle(props.pageTitle);
        } else if (props.label) {
            ui.actions.setPageTitle(props.label);
        } else {
            ui.actions.setPageTitle(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.pageTitle, props.label]);

    const rootClass = classnames(
        {
            [classes.root]: true,
            [classes.shadow]: props.showHeaderShadow,
        },
        props.className
    );

    const headerTextClass = classnames(classes.headerText, {
        [classes.pagePrimary]: props.pageType === "primary",
        [classes.pageSecondary]: props.pageType === "secondary",
    });

    return (
        <>
            <AppBar position="relative" className={rootClass}>
                <Toolbar
                    className={classes.toolbar}
                    id={"alex-text-id-toolbar"}
                >
                    {!!props.backUrl && (
                        <RouterLink to={props.backUrl}>
                            <PageActionButton
                                data-cy="backButton"
                                id="backButton"
                                title="back to search results"
                                color="primary"
                            >
                                <ArrowLeftIcon />
                            </PageActionButton>
                        </RouterLink>
                    )}
                    {props.headerInfo}

                    <div className={classes.labelOuter}>
                        <Tooltip placement="bottom-start" title={props.label}>
                            <div className={classes.labelInner}>
                                {!!props.labelLinkURL ? (
                                    <Link to={props.labelLinkURL}>
                                        <Text
                                            dataCy="pageHeaderTitle"
                                            variant="h1"
                                            className={headerTextClass}
                                        >
                                            {!!props.title
                                                ? props.title
                                                : props.label}
                                        </Text>
                                    </Link>
                                ) : (
                                    <Text
                                        dataCy="pageHeaderTitle"
                                        variant="h1"
                                        className={headerTextClass}
                                    >
                                        {!!props.title ? props.title : props.label}
                                    </Text>
                                )}

                                {!!props.statusLabel && (
                                    <span className={classes.statusLabel}>
                                        {props.statusLabel}
                                    </span>
                                )}
                            </div>
                        </Tooltip>
                        <div className={classes.subtitle}>{props.subtitle}</div>
                    </div>
                    <div className={classes.actions}>{props.children}</div>
                </Toolbar>
            </AppBar>

            {showLoading ? (
                <LinearProgress
                    data-cy="progressBar"
                    color="primary"
                    classes={{ root: classes.busy }}
                />
            ) : (
                <div className={classes.notBusy} />
            )}
        </>
    );
});
